import { Link, useLocation } from "react-router-dom";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import type { PublicEvent, PanchangDayMarkers, PanchangMarker } from "@vedaunion/shared";
import { modalLinkState } from "../../lib/modal-routing";
import { FullMoonIcon, NewMoonIcon } from "./lunarIcons";

/** Number of festival/observance names shown per grid cell before "+N". */
const MAX_CELL_MARKERS = 2;
const GENERIC_EKADASHI = new Set(["shukla-ekadashi", "krishna-ekadashi"]);

/**
 * Prioritised, de-duplicated marker list for a compact month-grid cell:
 *   - festivals/jayantis first, then saṅkrānti, then fasts (Ekādaśī), then moon;
 *   - the generic "Śukla/Krishna Ekādaśī" marker is dropped whenever a *named*
 *     Ekādaśī (e.g. Nirjalā) is present, so the cell never shows both.
 * The full, unabridged list still appears in the day panel when the cell is
 * tapped — this is only the at-a-glance subset.
 */
function notableMarkers(markers: PanchangMarker[]): PanchangMarker[] {
  const hasNamedEkadashi = markers.some(
    (m) => m.key.endsWith("-ekadashi") && !GENERIC_EKADASHI.has(m.key),
  );
  const filtered = hasNamedEkadashi
    ? markers.filter((m) => !GENERIC_EKADASHI.has(m.key))
    : markers;
  const rank = (k: PanchangMarker["kind"]) =>
    k === "festival" ? 0 : k === "sankranti" ? 1 : k === "fast" ? 2 : 3;
  return [...filtered].sort((a, b) => rank(a.kind) - rank(b.kind));
}

/**
 * Read-only month-grid calendar. Pure-CSS responsive — collapses event titles
 * gracefully on narrow widths. Click an event → goes to /calendar/:slug. The
 * cursor (current month) is owned by the parent.
 *
 * Polish over the previous version:
 *   - Multi-day events render as a connected pill (rounded only on the start
 *     day, square in the middle, square-end on the last day) so the eye sees
 *     the span.
 *   - Cancelled events get the strikethrough class — previously only on the
 *     detail page.
 *   - All-day events sit in a tinted strip with no time prefix.
 *   - Click an empty area of a day → invokes `onCreateAt` (if provided) with
 *     09:00 of that day, used by the dashboard to open the create form.
 *   - The "today" date number gets a small violet dot.
 *   - +N more becomes a clickable button that bumps the cap to all events.
 */

interface Props {
  cursor: Date;
  events: PublicEvent[];
  onCursorChange: (next: Date) => void;
  basePath?: string;
  onCreateAt?: (when: Date) => void;
  /** When false, suppresses the "MONTH YEAR · ‹ Today ›" header — the parent
   *  is rendering its own nav row and we don't want a duplicate. */
  showHeader?: boolean;
  /** Optional Vedic-calendar overlay: dayKey (yyyy-MM-dd) → markers. When
   *  present each cell shows its tithi + notable-day badge. */
  panchang?: Map<string, PanchangDayMarkers>;
  /** Called when a day cell is clicked (and not an event/button). Used by the
   *  public calendar to open that day's panchāṅga panel. */
  onSelectDay?: (dayKey: string) => void;
  /** Highlights the currently-selected day. */
  selectedDay?: string;
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface DayBucketEntry {
  ev: PublicEvent;
  /** Position within the multi-day span. `null` when the event fits one day. */
  segment: "start" | "middle" | "end" | "single" | null;
}

export function MonthGrid({ cursor, events, onCursorChange, basePath = "/calendar", onCreateAt, showHeader = true, panchang, onSelectDay, selectedDay }: Props) {
  const location = useLocation();
  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const cells: Date[] = [];
  let cur = gridStart;
  while (cur <= gridEnd) {
    cells.push(cur);
    cur = addDays(cur, 1);
  }

  // Bucket events by day-string (yyyy-MM-dd) and remember each day's position
  // within a multi-day event so the view can render continuation pills.
  const buckets = new Map<string, DayBucketEntry[]>();
  for (const ev of events) {
    const start = new Date(ev.startsAt);
    const end = new Date(ev.endsAt);
    let d = start;
    let safety = 0;
    while (d <= end && safety++ < 60) {
      const key = format(d, "yyyy-MM-dd");
      const list = buckets.get(key) ?? [];
      const segment: DayBucketEntry["segment"] = (() => {
        if (isSameDay(start, end)) return "single";
        if (isSameDay(d, start)) return "start";
        if (isSameDay(d, end)) return "end";
        return "middle";
      })();
      list.push({ ev, segment });
      buckets.set(key, list);
      d = addDays(d, 1);
    }
  }

  const today = new Date();

  return (
    <div className="month-grid-wrap">
      {showHeader && (
        <header className="month-grid-head">
          <h2 className="month-grid-title">{format(cursor, "MMMM yyyy")}</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="tt-btn"
              onClick={() => onCursorChange(subMonths(cursor, 1))}
              title="Previous month"
              aria-label="Previous month"
            >
              ‹
            </button>
            <button type="button" className="tt-btn" onClick={() => onCursorChange(new Date())} title="Today">
              Today
            </button>
            <button
              type="button"
              className="tt-btn"
              onClick={() => onCursorChange(addMonths(cursor, 1))}
              title="Next month"
              aria-label="Next month"
            >
              ›
            </button>
          </div>
        </header>
      )}

      <div className="cal-weekdays" aria-hidden>
        {DAY_LABELS.map((d) => (
          <div key={d} className="cal-head">
            {d}
          </div>
        ))}
      </div>

      <div className="cal-grid">
        {cells.map((d) => {
          const dayKey = format(d, "yyyy-MM-dd");
          const dayEvents = buckets.get(dayKey) ?? [];
          const out = !isSameMonth(d, cursor);
          const isToday = isSameDay(d, today);
          const hasOverflow = dayEvents.length > 3;
          const visible = hasOverflow ? dayEvents.slice(0, 3) : dayEvents;
          const pa = panchang?.get(dayKey);
          const isSelected = selectedDay === dayKey;
          const interactive = !!(onCreateAt || onSelectDay);
          return (
            <div
              key={dayKey}
              className={`cal-cell ${out ? "is-out" : ""} ${isToday ? "is-today" : ""} ${isSelected ? "is-selected" : ""}`}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("a, button")) return;
                if (onSelectDay) {
                  onSelectDay(dayKey);
                  return;
                }
                if (onCreateAt) {
                  const at = new Date(d);
                  at.setHours(9, 0, 0, 0);
                  onCreateAt(at);
                }
              }}
              role={interactive ? "button" : undefined}
              aria-label={
                onSelectDay
                  ? `Vedic calendar for ${format(d, "EEE d LLL")}`
                  : onCreateAt
                    ? `Add event on ${format(d, "EEE d LLL")}`
                    : undefined
              }
            >
              <span className="cal-cell-date">
                {format(d, "d")}
                {isToday && <span className="cal-today-dot" aria-hidden />}
              </span>
              {pa && (pa.markers.length > 0 || pa.tithi) && (() => {
                const notable = notableMarkers(pa.markers);
                const shown = notable.slice(0, MAX_CELL_MARKERS);
                const extra = notable.length - shown.length;
                const tithiEn = pa.tithi?.en;
                const moon =
                  tithiEn === "Purnima" ? "full" : tithiEn === "Amavasya" ? "new" : null;
                return (
                  <div className="cal-pa">
                    {shown.map((m) => (
                      <span
                        key={m.key}
                        className={`cal-pa-fest cal-pa-fest--${m.kind}`}
                        title={m.label}
                      >
                        {m.label}
                      </span>
                    ))}
                    {extra > 0 && (
                      <span className="cal-pa-fest-more" title={`${extra} more`}>
                        +{extra} more
                      </span>
                    )}
                    {pa.tithi && (
                      <span className="cal-pa-tithi" title={pa.tithi.iast}>
                        {moon === "full" && <FullMoonIcon size={9} className="cal-pa-moon" />}
                        {moon === "new" && (
                          <NewMoonIcon size={9} className="cal-pa-moon cal-pa-moon--new" />
                        )}
                        {pa.tithi.iast}
                      </span>
                    )}
                  </div>
                );
              })()}
              <div className="cal-cell-events">
                {visible.map(({ ev, segment }) => (
                  <Link
                    key={`${ev.occurrenceId}-${dayKey}`}
                    to={`${basePath}/${ev.slug}`}
                    state={modalLinkState(location)}
                    className={[
                      "cal-event",
                      ev.status === "cancelled" ? "is-cancelled" : "",
                      ev.allDay ? "is-allday" : "",
                      segment ? `is-${segment}` : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    data-cat={ev.category}
                    title={ev.title}
                  >
                    {!ev.allDay && segment !== "middle" && segment !== "end" && (
                      <span className="cal-event-time">{ev.timeTbd ? "TBA" : format(new Date(ev.startsAt), "HH:mm")}</span>
                    )}
                    <span className="cal-event-title">{ev.title}</span>
                  </Link>
                ))}
                {hasOverflow && (
                  <Link
                    to={`${basePath}?day=${dayKey}`}
                    className="cal-event-more"
                  >
                    +{dayEvents.length - 3} more
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
